import { cn } from '@/lib/utils';
import { OrderStatus, DriverStatus, ContainerStatus } from '@libs/schemas';

import { FC } from 'react';

// Define status types based on usage in the component
type StatusType = DriverStatus | OrderStatus | ContainerStatus;

// Hoisted out of the component so prose elsewhere can name a status with the same
// wording the badge uses, rather than restating it and drifting from it.
const statusDisplayInfo: { [key: string]: { class: string; label: string } } = {
  // Red statuses
  created: { class: 'text-red-800 bg-red-100', label: 'Created' },
  'pickup-requested': { class: 'text-red-800 bg-red-100', label: 'Ready for Pickup' },

  // Yellow statuses
  'drop-off-scheduled': { class: 'text-amber-800 bg-amber-100', label: 'Drop-off Scheduled' },
  'pickup-scheduled': { class: 'text-amber-800 bg-amber-100', label: 'Pickup scheduled' },

  // Blue status
  'drop-off-confirmed': { class: 'text-blue-800 bg-blue-100', label: 'Delivered' },

  // Green statuses
  completed: { class: 'text-emerald-800 bg-emerald-100', label: 'Completed' },
  refunded: { class: 'text-emerald-800 bg-emerald-100', label: 'Refunded' },

  // Gray statuses
  'in-transit-drop-off': { class: 'text-gray-800 bg-gray-100', label: 'Out for Delivery' },
  'in-transit-pickup': { class: 'text-gray-800 bg-gray-100', label: 'Out for Pick-up' },
  'pickup-confirmed': { class: 'text-gray-800 bg-gray-100', label: 'Dumpster Picked Up' },
  'waste-processing': { class: 'text-gray-800 bg-gray-100', label: 'Processing Waste' },
  cancelled: { class: 'text-gray-800 bg-gray-100', label: 'Cancelled' },
  'refund-failed': { class: 'text-gray-800 bg-gray-100', label: 'Refund Failed' },

  // Maintain other statuses with their existing styles
  active: { class: 'text-emerald-800 bg-emerald-100', label: 'Active' },
  available: { class: 'text-emerald-800 bg-emerald-100', label: 'Available' },
  'in-maintenance': { class: 'text-icon-warning bg-warning-muted', label: 'In Maintenance' },
  'in-transit': { class: 'text-icon-warning bg-warning-muted', label: 'In Transit' },
  'in-use': { class: 'text-icon-warning bg-warning-muted', label: 'In Use' },
  'out-of-service': { class: 'text-red-800 bg-red-100', label: 'Out of Service' },
  'payment-failed': { class: 'text-red-800 bg-red-100', label: 'Payment Failed' },
  'payment-pending': { class: 'text-icon-warning bg-warning-muted', label: 'Payment Pending' },
  'pending-drop-off': {
    class: 'text-icon-warning bg-warning-muted',
    label: 'Drop-off Schedule Pending',
  },
  restricted: { class: 'text-icon-warning bg-warning-muted', label: 'Hazardous Material' },
  suspended: { class: 'text-red-800 bg-red-100', label: 'Suspended' },
  'pending-pickup': {
    class: 'text-icon-warning bg-warning-muted',
    label: 'Pickup Schedule Pending',
  },
};

/**
 * The human-readable label for a status, matching the badge exactly. Falls back to the
 * raw status so an unmapped value still reads as itself rather than as "Unknown Status"
 * in the middle of a sentence.
 */
export const getStatusDisplayLabel = (status: StatusType): string =>
  statusDisplayInfo[status]?.label ?? status;

export const StatusDisplay: FC<{
  status: StatusType;
  displayTextOverride?: string;
  className?: string;
}> = ({ status, displayTextOverride, className = 'text-sm' }) => {
  // Defensive programming: handle cases where status is not found
  const statusInfo = statusDisplayInfo[status];
  if (!statusInfo) {
    return (
      <div
        className={cn(
          `py-[2px] px-[8px] rounded-lg h-fit w-fit`,
          'text-gray-800 bg-gray-100',
          className,
        )}
      >
        {displayTextOverride || 'Unknown Status'}
      </div>
    );
  }

  return (
    <div className={cn(`py-[2px] px-[8px] rounded-lg h-fit w-fit`, statusInfo.class, className)}>
      {displayTextOverride || statusInfo.label}
    </div>
  );
};
